import { useState, type ReactNode, useRef, useEffect, useTransition } from 'react';

type VirtualizeProps = {
    height?: number | string;
    width?: number | string;
    children?: ReactNode;
    root: HTMLElement | null;
    /**
     * When set to `true`, the component will act as a passthrough and render
     * any provided children with no virtualization.
     */
    ignore?: boolean;
};

export const Virtualize = ({
    ignore = false,
    children,
    root,
    height = 50,
    width = 200,
}: VirtualizeProps): JSX.Element => {
    const [visible, setVisible] = useState(false);
    const intersector = useRef<HTMLDivElement | null>(null);
    const [isPending, startTransition] = useTransition();

    useEffect(() => {
        if (ignore) return;

        if (!intersector.current || !root) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                startTransition(() => {
                    setVisible(entry.isIntersecting);
                });
            },
            { root, rootMargin: '1000px 0px 1000px 0px' }
        );

        observer.observe(intersector.current);

        return () => {
            if (!intersector.current) return;
            observer.unobserve(intersector.current);
        };
    }, [intersector, root]);

    return ignore ? (
        <>{children}</>
    ) : (
        <div
            className='virtualize'
            ref={intersector}
            style={{
                height: visible && !isPending ? 'auto' : height,
                width: visible && !isPending ? 'auto' : width,
            }}>
            {(visible && !isPending) || !intersector.current ? children : null}
        </div>
    );
};
